export type Theme = "light" | "dark";

const STORAGE_KEY = "alphahedge-theme";
// A1 is a dark brand; light is an explicit, remembered opt-in (index.html
// bootstrap applies the same rule before the first paint).
const DEFAULT_THEME: Theme = "dark";
const THEME_COLOR: Record<Theme, string> = { dark: "#080d11", light: "#f1f4f0" };

function parsePreference(value: string | null): Theme | null {
  return value === "light" || value === "dark" ? value : null;
}

function readPreference(): Theme | null {
  try { return parsePreference(window.localStorage.getItem(STORAGE_KEY)); }
  catch { return null; }
}

function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  document.documentElement.style.backgroundColor = THEME_COLOR[theme];
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[theme]);
  document.querySelector('meta[name="color-scheme"]')?.setAttribute("content", theme);
}

export function createThemeStore() {
  let theme: Theme = readPreference() ?? DEFAULT_THEME;
  const listeners = new Set<() => void>();

  const publish = (next: Theme) => {
    theme = next;
    applyTheme(next);
    listeners.forEach(listener => listener());
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY && event.key !== null) return;
    try { if (event.storageArea && event.storageArea !== window.localStorage) return; }
    catch { /* Storage access can be blocked while the page is open. */ }
    publish((event.key === null ? null : parsePreference(event.newValue)) ?? DEFAULT_THEME);
  };

  return {
    getSnapshot: () => theme,
    toggle: () => {
      const next: Theme = theme === "dark" ? "light" : "dark";
      try { window.localStorage.setItem(STORAGE_KEY, next); }
      catch { /* A manual choice still applies for this page session. */ }
      publish(next);
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      if (listeners.size === 1) {
        window.addEventListener("storage", onStorage);
        // Reconcile a choice made in another tab before subscribing.
        publish(readPreference() ?? DEFAULT_THEME);
      }
      return () => {
        listeners.delete(listener);
        if (!listeners.size) window.removeEventListener("storage", onStorage);
      };
    },
  };
}
